import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ListingChangePayload } from "@colab/shared";
import { apiFetch, type ApiClientError } from "@/lib/api-client";
import { formatApiError } from "@/lib/format-api-error";
import { queryKeys } from "@/lib/query-keys";

// PUT /api/portal/listing/changes — submit a listing change request for
// Bookboost review. Pessimistic (submissions convention): no optimistic
// update; on success we invalidate the single portal listing query so the
// pending banner appears from the server's answer. The success toast fires
// in the page component, not here — mirrors useSubmitDeal.
//
// Worker 409s worth knowing about (all surfaced via the error toast):
// not_linked / pending_conflict / marketplace_not_configured.
export function useSubmitListingChange() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true; id: string }, ApiClientError, ListingChangePayload>({
    mutationFn: (payload) =>
      apiFetch<{ ok: true; id: string }>("/portal/listing/changes", {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.listing() });
    },
    onError: (err) => {
      toast.error(formatApiError(err, "Failed to submit changes"));
    },
  });
}
